import React, {useCallback, useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {useNavigate} from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import {getUser} from "../../js/User";
import {clearCredentialsStorage, userId as storedUserId} from "../../js/services/LoginService";
import {buildAppPageUrl, buildSettingsTabUrl, DEFAULT_SETTINGS_TAB, PROFILE_PATH} from "../../constants/navigation";

export const getInitials = (user) => {
    const first = (user?.firstName || "").trim();
    const last = (user?.lastName || "").trim();
    const initials = `${first.charAt(0)}${last.charAt(0)}`.trim();
    if (initials) {
        return initials.toUpperCase();
    }
    return (user?.email || "").trim().charAt(0).toUpperCase() || "?";
};

const displayName = (user) => {
    const name = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    return name.trim() || user?.email || "";
};

/**
 * Header user menu: avatar + name, with links to the personal profile page,
 * Account & billing, company settings and logout (desktop and mobile).
 */
const UserMenu = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const id = storedUserId();
        if (!id) {
            return undefined;
        }

        let cancelled = false;
        getUser(id)
            .then((data) => {
                if (!cancelled) {
                    setUser(data || null);
                }
            })
            .catch(() => {
                // The menu still works with initials "?" and without the email.
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    const go = useCallback((target) => {
        setOpen(false);
        navigate(target);
    }, [navigate]);

    const handleLogout = useCallback(() => {
        setOpen(false);
        clearCredentialsStorage();
        navigate("/");
    }, [navigate]);

    const name = displayName(user);

    const itemClasses = "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus:outline-hidden focus-visible:bg-slate-100";

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((previous) => !previous)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={intl.formatMessage({id: "usermenu.trigger"})}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-1 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 sm:pr-3"
            >
                <span
                    aria-hidden="true"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700"
                >
                    {getInitials(user)}
                </span>
                {name && <span className="hidden max-w-[10rem] truncate sm:block">{name}</span>}
                <ExpandMoreRoundedIcon fontSize="small" className={`hidden text-slate-400 transition sm:block ${open ? "rotate-180" : ""}`}/>
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute right-0 z-50 mt-2 w-[260px] max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl"
                >
                    {(name || user?.email) && (
                        <div className="border-b border-slate-100 px-3 pb-2 pt-1">
                            {name && <p className="truncate text-sm font-semibold text-slate-900">{name}</p>}
                            {user?.email && <p className="truncate text-xs text-slate-500">{user.email}</p>}
                        </div>
                    )}
                    <div className="flex flex-col gap-0.5 pt-1">
                        <button type="button" role="menuitem" className={itemClasses} onClick={() => go(PROFILE_PATH)}>
                            <PersonRoundedIcon fontSize="small" className="text-slate-400"/>
                            {intl.formatMessage({id: "usermenu.profile"})}
                        </button>
                        <button type="button" role="menuitem" className={itemClasses} onClick={() => go(buildAppPageUrl("account"))}>
                            <ManageAccountsRoundedIcon fontSize="small" className="text-slate-400"/>
                            {intl.formatMessage({id: "sidebar.account.billing"})}
                        </button>
                        <button type="button" role="menuitem" className={itemClasses} onClick={() => go(buildSettingsTabUrl(DEFAULT_SETTINGS_TAB))}>
                            <TuneRoundedIcon fontSize="small" className="text-slate-400"/>
                            {intl.formatMessage({id: "settings.hero.title"})}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleLogout}
                            className="mt-1 flex w-full items-center gap-3 rounded-xl border-t border-slate-100 px-3 py-2 text-left text-sm font-semibold text-rose-600 transition hover:bg-rose-50 focus:outline-hidden"
                        >
                            <LogoutRoundedIcon fontSize="small"/>
                            {intl.formatMessage({id: "sidebar.logout"})}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserMenu;
